import _extends from "@babel/runtime/helpers/extends";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import React, { lazy, useContext, useState } from 'react';
import { useUID } from 'react-uid';
import { PortalConsumer, RendererContext } from '../../../context';
import { FormStateChangeNotifier } from '../form';
import { Label } from '../label';
import { useIsInForm } from '../utils/useIsInForm';
var BITBUCKET_FIELD_ID = 'BitbucketMentions';
var AKFormField = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form/Field');
});
var AKUserPicker = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.user-picker' */
  '@atlaskit/user-picker');
});
var AKSmartUserPicker = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.smart-user-picker' */
  '@atlaskit/smart-user-picker');
});
var AKHelperMessage = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form').then(function (module) {
    return {
      default: module.HelperMessage
    };
  });
});
var AKErrorMessage = /*#__PURE__*/lazy(function () {
  return import( /* webpackChunkName: '@atlaskit-internal_.form' */
  '@atlaskit/form').then(function (module) {
    return {
      default: module.ErrorMessage
    };
  });
});
var validate = function validate(value) {
  return !value || Array.isArray(value) && value.length === 0 ? 'EMPTY' : undefined;
};
// the value prop in UserPicker component can only take undefined for empty value.
// null is not allowed and will resulted in unexpected behavior.
var ensureUserPickerValue = function ensureUserPickerValue(value) {
  if (!value || Array.isArray(value) && value.length === 0) {
    return undefined;
  }
  return value;
};

// by forcing a key update when defaultValue is present, we force the SmartUserPicker to re-render.
// this is because the SmartUserPicker does not re-render when the defaultValue changes in its
// current implementation.
var makeUserPickerKey = function makeUserPickerKey(id, defaultValue) {
  if (!defaultValue || Array.isArray(defaultValue) && defaultValue.length === 0) {
    return id;
  }
  return "".concat(id, "-with-default");
};
var RawUserPicker = function RawUserPicker(_ref) {
  var id = _ref.id,
    description = _ref.description,
    placeholder = _ref.placeholder,
    isMulti = _ref.isMulti,
    _ref$includeUsers = _ref.includeUsers,
    includeUsers = _ref$includeUsers === void 0 ? true : _ref$includeUsers,
    _ref$includeGroups = _ref.includeGroups,
    includeGroups = _ref$includeGroups === void 0 ? false : _ref$includeGroups,
    currentAccountId = _ref.accountId,
    suppliedCloudId = _ref.cloudId,
    productKey = _ref.productKey,
    baseUrl = _ref.baseUrl,
    _onChange = _ref.onChange,
    query = _ref.query,
    defaultValue = _ref.defaultValue,
    providedValue = _ref.value,
    width = _ref.width,
    productAttributes = _ref.productAttributes;
  var _useState = useState(defaultValue),
    _useState2 = _slicedToArray(_useState, 2),
    value = _useState2[0],
    setValue = _useState2[1];
  return /*#__PURE__*/React.createElement(PortalConsumer, null, function (portal) {
    return /*#__PURE__*/React.createElement(React.Fragment, null, suppliedCloudId && productKey ? /*#__PURE__*/React.createElement(AKSmartUserPicker, {
      key: makeUserPickerKey(id, defaultValue),
      value: ensureUserPickerValue(providedValue !== null && providedValue !== void 0 ? providedValue : value),
      onChange: function onChange(user) {
        setValue(user);
        _onChange === null || _onChange === void 0 || _onChange(user);
      },
      isMulti: isMulti,
      menuPortalTarget: portal || undefined,
      noOptionsMessage: function noOptionsMessage() {
        return null;
      },
      placeholder: placeholder,
      width: width,
      fieldId:
      // Bitbucket User Recommendations does not support generic contextType
      productKey === 'bitbucket' ? BITBUCKET_FIELD_ID : id,
      debounceTime: 400,
      includeUsers: includeUsers,
      includeGroups: includeGroups,
      siteId: suppliedCloudId,
      productAttributes: productAttributes
      // If principalId === 'Context', upstream attempts to use other context
      // to discover the principal ID
      ,
      principalId: currentAccountId !== null && currentAccountId !== void 0 ? currentAccountId : 'Context',
      productKey: productKey,
      baseUrl: baseUrl,
      inputId: id,
      defaultValue: defaultValue
    }) : /*#__PURE__*/React.createElement(AKUserPicker, {
      value: ensureUserPickerValue(providedValue !== null && providedValue !== void 0 ? providedValue : value),
      onChange: function onChange(user) {
        setValue(user);
        _onChange === null || _onChange === void 0 || _onChange(user);
      },
      isMulti: isMulti,
      menuPortalTarget: portal || undefined,
      noOptionsMessage: function noOptionsMessage() {
        return null;
      },
      placeholder: placeholder,
      width: width,
      loadOptions: query,
      fieldId: null,
      inputId: id,
      defaultValue: defaultValue
    }), description && /*#__PURE__*/React.createElement(AKHelperMessage, null, description));
  });
};
var FormUserPicker = function FormUserPicker(_ref2) {
  var id = _ref2.id,
    name = _ref2.name,
    label = _ref2.label,
    description = _ref2.description,
    placeholder = _ref2.placeholder,
    isRequired = _ref2.isRequired,
    isMulti = _ref2.isMulti,
    _ref2$includeUsers = _ref2.includeUsers,
    includeUsers = _ref2$includeUsers === void 0 ? true : _ref2$includeUsers,
    _ref2$includeGroups = _ref2.includeGroups,
    includeGroups = _ref2$includeGroups === void 0 ? false : _ref2$includeGroups,
    accountId = _ref2.accountId,
    cloudId = _ref2.cloudId,
    productKey = _ref2.productKey,
    baseUrl = _ref2.baseUrl,
    _onChange2 = _ref2.onChange,
    query = _ref2.query,
    defaultValue = _ref2.defaultValue,
    width = _ref2.width,
    productAttributes = _ref2.productAttributes;
  return /*#__PURE__*/React.createElement(AKFormField, {
    id: "".concat(id, "-forge-").concat(cloudId && productKey ? 'smart-user-picker' : 'user-picker'),
    name: name,
    label: label,
    defaultValue: defaultValue,
    validate: isRequired ? validate : undefined,
    isRequired: isRequired
  }, function (_ref3) {
    var fieldProps = _ref3.fieldProps,
      error = _ref3.error;
    var id = fieldProps.id,
      value = fieldProps.value;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FormStateChangeNotifier, {
      name: name,
      value: value
    }), /*#__PURE__*/React.createElement(RawUserPicker, {
      id: id,
      name: name,
      label: label,
      value: value,
      placeholder: placeholder,
      isMulti: isMulti,
      includeUsers: includeUsers,
      includeGroups: includeGroups,
      accountId: accountId,
      cloudId: cloudId,
      productAttributes: productAttributes,
      productKey: productKey,
      baseUrl: baseUrl,
      onChange: function onChange(user) {
        fieldProps.onChange(user);
        _onChange2 === null || _onChange2 === void 0 || _onChange2(user);
      },
      query: query,
      defaultValue: defaultValue,
      width: width
    }), error === 'EMPTY' && /*#__PURE__*/React.createElement(AKErrorMessage, null, "This field is required."), description && /*#__PURE__*/React.createElement(AKHelperMessage, null, description));
  });
};

// This is a wrapper containing the core logic of user picker
// It has been separated to take query as props so that it can be testable in Jest and rendered in Storybook
export var UserPicker = function UserPicker(props) {
  var uid = useUID();
  var _useContext = useContext(RendererContext),
    forgeReactMajorVersion = _useContext.forgeReactMajorVersion;
  var width = forgeReactMajorVersion && forgeReactMajorVersion >= 10 ? '100%' : '320px';
  var isInForm = useIsInForm();
  if (!isInForm) {
    var name = props.name,
      isRequired = props.isRequired,
      label = props.label;
    return /*#__PURE__*/React.createElement(React.Fragment, null, label && /*#__PURE__*/React.createElement(Label, {
      name: name,
      isRequired: isRequired,
      label: label
    }), /*#__PURE__*/React.createElement(RawUserPicker, _extends({
      id: uid,
      width: width
    }, props)));
  }
  return /*#__PURE__*/React.createElement(FormUserPicker, _extends({
    id: uid,
    width: width
  }, props));
};